import { ArrowLeft, RefreshCw, Skull } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'

/**
 * Arcade "GAME OVER" 404. Massive glitching numerals with chromatic
 * aberration, terminal-style status panel, fake K/D stat block, blinking
 * caret prompt. The path the user mis-routed to is echoed back as part of
 * the "connection lost" readout, which keeps the joke grounded.
 */
export default function NotFound() {
  const location = useLocation()
  const navigate = useNavigate()

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center overflow-hidden bg-bg px-6 py-16">
      <BackgroundAtmosphere />
      <ScanLineOverlay />

      <div className="relative z-10 w-full max-w-2xl">
        {/* Alert ribbon */}
        <div className="mb-10 flex items-center justify-center">
          <span className="animate-flicker inline-flex items-center gap-2 rounded-full border border-danger/40 bg-danger/10 px-3 py-1 font-display text-[11px] font-semibold tracking-[0.25em] text-danger uppercase">
            <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-danger" />
            Connection lost
          </span>
        </div>

        {/* Glitching 404 — three stacked layers create the chromatic offset */}
        <Glitch404 />

        {/* Caption */}
        <p className="font-display mt-6 text-center text-2xl font-semibold tracking-tight md:text-3xl">
          <span className="text-fg">Route not found.</span>{' '}
          <span className="text-fg-muted">You got fragged.</span>
        </p>

        {/* Terminal panel */}
        <div className="font-mono mx-auto mt-10 w-full max-w-md overflow-hidden rounded-xl border border-border bg-surface text-[12px] leading-relaxed text-fg-muted shadow-2xl shadow-black/40">
          <div className="flex items-center justify-between border-b border-border bg-bg/60 px-4 py-2">
            <div className="flex items-center gap-1.5">
              <span className="block h-2.5 w-2.5 rounded-full bg-danger/80" />
              <span className="block h-2.5 w-2.5 rounded-full bg-warning/80" />
              <span className="block h-2.5 w-2.5 rounded-full bg-success/80" />
            </div>
            <span className="font-display text-[10px] tracking-[0.25em] text-fg-subtle uppercase">
              ops.log
            </span>
          </div>
          <div className="space-y-1 px-4 py-3">
            <p>
              <span className="text-fg-subtle">$</span>{' '}
              <span className="text-primary">GET</span>{' '}
              <span className="text-fg">{truncate(location.pathname, 32)}</span>
            </p>
            <p className="text-danger">
              <Skull className="mr-1 inline-block size-3 -translate-y-px" />
              ERR_404 · resource fragged
            </p>
            <p>
              <span className="text-fg-subtle">$</span>{' '}
              <span className="text-fg">trace --hops 1</span>
            </p>
            <p className="text-fg-muted">
              ↳ no path on this bracket. it never advanced past round one.
            </p>
            <p className="flex items-center">
              <span className="text-fg-subtle">$</span>
              <span aria-hidden className="animate-caret ml-1 inline-block h-3 w-2 bg-primary" />
            </p>
          </div>
        </div>

        {/* Fake K/D scorecard */}
        <Scorecard />

        {/* Actions */}
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild variant="primary" size="lg">
            <Link to="/">
              <ArrowLeft className="size-4" aria-hidden />
              Regroup at base
            </Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => navigate(-1)}
          >
            <RefreshCw className="size-4" aria-hidden />
            Try respawn
          </Button>
        </div>

        {/* Final whisper */}
        <p className="mt-12 text-center font-display text-[10px] tracking-[0.3em] text-fg-subtle uppercase">
          <span className="text-primary">//</span> press start to continue
        </p>
      </div>
    </main>
  )
}

function Glitch404() {
  // Three identical "404" stacks: a cyan-magenta chromatic aberration
  // sandwich with a glitching offset on the centre layer. Aria-hidden on
  // the decorative layers; the visible 404 is screen-reader-announced once.
  return (
    <div className="relative mx-auto flex h-[clamp(8rem,28vw,16rem)] items-center justify-center">
      <span
        aria-hidden
        className="absolute font-display text-[clamp(8rem,28vw,16rem)] leading-none font-black tracking-tighter text-secondary/70 mix-blend-screen"
        style={{ transform: 'translate(-6px, 2px)', filter: 'blur(0.5px)' }}
      >
        404
      </span>
      <span
        aria-hidden
        className="absolute font-display text-[clamp(8rem,28vw,16rem)] leading-none font-black tracking-tighter text-primary/70 mix-blend-screen"
        style={{ transform: 'translate(6px, -2px)', filter: 'blur(0.5px)' }}
      >
        404
      </span>
      <span
        className="animate-glitch font-display gradient-text relative text-[clamp(8rem,28vw,16rem)] leading-none font-black tracking-tighter"
        role="img"
        aria-label="404 — not found"
      >
        404
      </span>
    </div>
  )
}

function Scorecard() {
  const rows = [
    { label: 'Frags', value: '0' },
    { label: 'Assists', value: '0' },
    { label: 'Deaths', value: '1' },
    { label: 'K/D', value: '0.00' },
  ]
  return (
    <div className="mx-auto mt-8 grid w-full max-w-md grid-cols-4 overflow-hidden rounded-xl border border-border bg-surface">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex flex-col items-center gap-1 border-r border-border px-3 py-3 last:border-r-0"
        >
          <span className="font-display text-[10px] tracking-[0.25em] text-fg-subtle uppercase">
            {row.label}
          </span>
          <span className="font-display text-2xl font-bold tabular-nums text-fg">
            {row.value}
          </span>
        </div>
      ))}
    </div>
  )
}

/** Background mesh + dual radial glows — atmosphere only. */
function BackgroundAtmosphere() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            'radial-gradient(circle at center, rgba(245,245,245,0.6) 1px, transparent 1.5px)',
          backgroundSize: '28px 28px',
          maskImage:
            'radial-gradient(ellipse 70% 60% at 50% 50%, #000 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 70% 60% at 50% 50%, #000 30%, transparent 80%)',
        }}
      />
      <div
        className="absolute top-1/3 -left-32 h-[480px] w-[480px] -translate-y-1/2 rounded-full opacity-30 blur-3xl"
        style={{ backgroundColor: 'var(--color-primary)' }}
      />
      <div
        className="absolute top-1/3 -right-32 h-[440px] w-[440px] -translate-y-1/2 rounded-full opacity-30 blur-3xl"
        style={{ backgroundColor: 'var(--color-secondary)' }}
      />
    </div>
  )
}

/** Slowly drifting horizontal scan line — CRT atmosphere. */
function ScanLineOverlay() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <div
        className="animate-scanlines absolute inset-x-0 h-32 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(180deg, transparent 0%, rgba(245,245,245,0.9) 50%, transparent 100%)',
        }}
      />
      {/* Fine repeating scan-line texture */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, rgba(245,245,245,0.7) 0px, rgba(245,245,245,0.7) 1px, transparent 1px, transparent 3px)',
        }}
      />
    </div>
  )
}

function truncate(text: string, max: number) {
  if (text.length <= max) return text
  return `${text.slice(0, max - 1)}…`
}
